import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, Lock, Wallet, MapPin, ArrowLeft, Truck, Banknote, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import { eddText, shippingText } from "@/components/DeliveryCheck";

const inputCls = "bg-zinc-100 border border-zinc-200 text-zinc-900 text-xs px-3.5 py-2.5 focus:outline-none focus:border-gold/60 transition-colors duration-300 w-full";

const FREE_SHIP_MIN = 999;

const FreeShipMeter = ({ total }) => {
  const remaining = Math.max(0, FREE_SHIP_MIN - total);
  const pct = Math.min(100, (total / FREE_SHIP_MIN) * 100);
  return (
    <div className="border border-pink-100 bg-pink-50/60 px-4 py-3.5" data-testid="free-ship-meter">
      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">
        {remaining > 0 ? (
          <>Add <span className="text-gold font-medium">{inr(remaining)}</span> more for <span className="text-gold font-medium">FREE shipping</span></>
        ) : (
          <span className="text-emerald-600">You've unlocked FREE shipping</span>
        )}
      </p>
      <div className="mt-2.5 h-1 bg-pink-100 overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${remaining > 0 ? "bg-gold" : "bg-emerald-500"}`}
          style={{ width: `${pct}%` }}
          data-testid="free-ship-bar"
        />
      </div>
    </div>
  );
};

export const CartDrawer = () => {
  const { items, total, isOpen, setIsOpen, setQty, remove, clear, couponCode, setCouponCode } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [step, setStep] = useState("bag");
  const [addresses, setAddresses] = useState([]);
  const [selectedAddr, setSelectedAddr] = useState("new");
  const [saveAddr, setSaveAddr] = useState(true);
  const [applied, setApplied] = useState(null);
  const [custName, setCustName] = useState("");
  const [custEmail, setCustEmail] = useState("");
  const [custPhone, setCustPhone] = useState("");
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [shipQuote, setShipQuote] = useState(null);
  const [payMethod, setPayMethod] = useState("card");
  const [codFee, setCodFee] = useState(0);

  useEffect(() => {
    if (pincode.length !== 6 || items.length === 0) {
      setShipQuote(null);
      return;
    }
    const t = setTimeout(() => {
      api.post("/shipping/estimate", { pincode, items: items.map((i) => ({ product_id: i.id, quantity: i.qty })) })
        .then((r) => setShipQuote(r.data))
        .catch(() => setShipQuote(null));
    }, 500);
    return () => clearTimeout(t);
  }, [pincode, items]);

  const shippingCharge = shipQuote?.charge || 0;

  useEffect(() => {
    if (user) {
      setCustName(user.name || "");
      setCustEmail(user.email || "");
    }
  }, [user]);

  useEffect(() => {
    if (isOpen && user) {
      api.get("/wallet").then((r) => setWallet(r.data)).catch(() => setWallet(null));
      api.get("/addresses").then((r) => {
        setAddresses(r.data);
        if (r.data.length > 0) pickAddress(r.data[0]);
      }).catch(() => {});
    }
    if (isOpen) {
      api.get("/settings").then((r) => setCodFee(Number(r.data.cod_fee) || 0)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  const pickAddress = (a) => {
    setSelectedAddr(a.id);
    setCustName(a.name);
    setCustPhone(a.phone);
    setLine1(a.line1);
    setLine2(a.line2 || "");
    setCity(a.city);
    setState(a.state);
    setPincode(a.pincode);
  };

  const finalTotal = (applied ? applied.final : total) + shippingCharge;

  const applyCouponCode = async (silent = false) => {
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: couponCode, amount: total });
      setApplied(data);
      if (!silent) toast.success(`${data.code} applied — you save ${inr(data.discount)}`);
    } catch (err) {
      setApplied(null);
      if (!silent) toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (isOpen && couponCode.trim() && !applied && items.length > 0) applyCouponCode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const payload = () => ({
    items: items.map((i) => ({ product_id: i.id, quantity: i.qty, variant: i.variant || "" })),
    origin_url: window.location.origin,
    customer_name: custName,
    customer_email: custEmail,
    customer_phone: custPhone,
    ship_line1: line1,
    ship_line2: line2,
    ship_city: city,
    ship_state: state,
    ship_pincode: pincode,
    coupon_code: applied?.code || "",
  });

  const validate = () => {
    if (!custName.trim() || !custEmail.trim() || !custPhone.trim() || !line1.trim() || !city.trim() || !state.trim() || !pincode.trim()) {
      toast.error("Please fill in your contact and delivery address");
      return false;
    }
    return true;
  };

  const maybeSaveAddress = async () => {
    if (!user || !saveAddr || selectedAddr !== "new") return;
    try {
      await api.post("/addresses", { name: custName, phone: custPhone, line1, line2, city, state, pincode });
    } catch {}
  };

  const checkout = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await maybeSaveAddress();
      const { data } = await api.post("/payments/cart-checkout", payload());
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const walletPay = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await maybeSaveAddress();
      await api.post("/payments/wallet-checkout", payload());
      toast.success("Order placed with your ViRa Wallet");
      clear();
      setApplied(null);
      setIsOpen(false);
      setStep("bag");
      navigate("/orders");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const codPay = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await maybeSaveAddress();
      await api.post("/payments/cod-checkout", payload());
      toast.success("Order placed — pay when it arrives");
      clear();
      setApplied(null);
      setIsOpen(false);
      setStep("bag");
      navigate("/orders");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const walletEligible = user && wallet && wallet.balance >= finalTotal && finalTotal > 0;
  const codFeeAmt = payMethod === "cod" ? codFee : 0;
  const displayTotal = finalTotal + codFeeAmt;
  const placeOrder = payMethod === "wallet" ? walletPay : payMethod === "cod" ? codPay : checkout;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-zinc-900/40 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[85] w-full sm:max-w-md bg-white border-l border-zinc-200 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 sm:px-8 py-6 border-b border-zinc-200">
              <h2 className="font-serif font-medium text-3xl text-zinc-900">
                {step === "bag" ? "Your Bag" : "Delivery Address"}
              </h2>
              <button onClick={() => setIsOpen(false)} data-testid="cart-close-btn" className="text-zinc-500 hover:text-gold transition-colors duration-300" aria-label="Close bag">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-6 sm:px-8 py-6 space-y-6" data-lenis-prevent>
              {items.length === 0 ? (
                <p className="font-serif italic text-xl text-zinc-400 pt-16 text-center" data-testid="cart-empty">
                  Your bag is empty — for now.
                </p>
              ) : step === "bag" ? (
                <>
                  <FreeShipMeter total={applied ? applied.final : total} />
                  {items.map((item) => (
                  <div key={`${item.id}|${item.variant || ""}`} className="flex gap-5 border-b border-zinc-100 pb-6" data-testid={`cart-item-${item.id}${item.variant ? `-${item.variant}` : ""}`}>
                    <div className="w-16 h-20 bg-zinc-100 border border-zinc-100 shrink-0 flex items-center justify-center">
                      <img src={item.image} alt="" className="max-w-full max-h-full object-contain" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-800 text-sm leading-snug line-clamp-2">{item.name}</p>
                      {item.variant && <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-1" data-testid={`cart-variant-${item.id}`}>{item.variant}</p>}
                      <p className="text-gold text-sm mt-1.5">{inr(item.price)}</p>
                      <div className="flex items-center gap-3 mt-3">
                        <button onClick={() => setQty(item.id, item.qty - 1, item.variant || "")} data-testid={`cart-minus-${item.id}`} className="p-1.5 border border-zinc-200 text-zinc-500 hover:border-gold hover:text-gold transition-colors duration-200" aria-label="Decrease">
                          <Minus size={12} />
                        </button>
                        <span className="text-zinc-700 text-sm w-5 text-center">{item.qty}</span>
                        <button onClick={() => setQty(item.id, item.qty + 1, item.variant || "")} data-testid={`cart-plus-${item.id}`} className="p-1.5 border border-zinc-200 text-zinc-500 hover:border-gold hover:text-gold transition-colors duration-200" aria-label="Increase">
                          <Plus size={12} />
                        </button>
                        <button onClick={() => remove(item.id, item.variant || "")} data-testid={`cart-remove-${item.id}`} className="ml-auto p-1.5 text-zinc-400 hover:text-red-400 transition-colors duration-200" aria-label="Remove">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                  ))}
                </>
              ) : (
                <div className="space-y-4" data-testid="address-step">
                  {user && addresses.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">Saved Addresses</p>
                      {addresses.map((a) => (
                        <button
                          key={a.id}
                          onClick={() => pickAddress(a)}
                          data-testid={`saved-address-${a.id}`}
                          className={`w-full text-left border p-4 transition-colors duration-200 ${selectedAddr === a.id ? "border-gold bg-pink-50/50" : "border-zinc-200 hover:border-gold/40"}`}
                        >
                          <p className="text-sm text-zinc-800 font-medium flex items-center gap-2"><MapPin size={13} className="text-gold" />{a.name}</p>
                          <p className="text-xs text-zinc-500 mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pincode}</p>
                        </button>
                      ))}
                      <button
                        onClick={() => { setSelectedAddr("new"); setLine1(""); setLine2(""); setCity(""); setState(""); setPincode(""); }}
                        data-testid="new-address-btn"
                        className={`text-[10px] uppercase tracking-[0.25em] ${selectedAddr === "new" ? "text-gold" : "text-zinc-400 hover:text-gold"} transition-colors duration-200`}
                      >
                        + Use a new address
                      </button>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <input value={custName} onChange={(e) => setCustName(e.target.value)} placeholder="Full name *" data-testid="cart-name-input" className={inputCls} />
                    <input value={custPhone} onChange={(e) => setCustPhone(e.target.value)} placeholder="Phone *" data-testid="cart-phone-input" className={inputCls} />
                    <input value={custEmail} onChange={(e) => setCustEmail(e.target.value)} placeholder="Email for order updates *" type="email" data-testid="cart-email-input" className={`${inputCls} col-span-2`} />
                    <input value={line1} onChange={(e) => setLine1(e.target.value)} placeholder="Address line 1 *" data-testid="ship-line1-input" className={`${inputCls} col-span-2`} />
                    <input value={line2} onChange={(e) => setLine2(e.target.value)} placeholder="Address line 2 (optional)" data-testid="ship-line2-input" className={`${inputCls} col-span-2`} />
                    <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City *" data-testid="ship-city-input" className={inputCls} />
                    <input value={state} onChange={(e) => setState(e.target.value)} placeholder="State *" data-testid="ship-state-input" className={inputCls} />
                    <input value={pincode} onChange={(e) => setPincode(e.target.value)} placeholder="Pincode *" data-testid="ship-pincode-input" className={inputCls} />
                  </div>
                  {user && selectedAddr === "new" && (
                    <label className="flex items-center gap-2.5 text-xs text-zinc-500">
                      <input type="checkbox" checked={saveAddr} onChange={(e) => setSaveAddr(e.target.checked)} data-testid="save-address-check" className="accent-[#E80071] w-3.5 h-3.5" />
                      Save to my address book
                    </label>
                  )}
                  {shipQuote && (
                    <p className="text-xs text-zinc-500 flex items-center gap-2" data-testid="checkout-edd">
                      <Truck size={13} className="text-gold shrink-0" /> Estimated delivery {eddText(shipQuote)} · {shippingText(shipQuote)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-zinc-200 px-6 sm:px-8 py-6">
                <div className="flex gap-2 mb-5">
                  <input
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value); setApplied(null); }}
                    placeholder="Coupon code"
                    data-testid="coupon-input"
                    className="flex-1 bg-zinc-100 border border-zinc-200 text-zinc-900 text-xs uppercase tracking-widest px-3.5 py-2.5 focus:outline-none focus:border-gold/60 transition-colors duration-300"
                  />
                  <button
                    onClick={applyCouponCode}
                    data-testid="coupon-apply-btn"
                    className="text-[10px] uppercase tracking-[0.25em] border border-gold/40 text-gold px-5 hover:bg-gold hover:text-white transition-colors duration-300"
                  >
                    Apply
                  </button>
                </div>
                <div className="flex justify-between items-baseline mb-1.5">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Subtotal</span>
                  <span className="text-zinc-600">{inr(total)}</span>
                </div>
                {applied && (
                  <div className="flex justify-between items-baseline mb-1.5" data-testid="coupon-discount-row">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-600">Coupon {applied.code}</span>
                    <span className="text-emerald-600">−{inr(applied.discount)}</span>
                  </div>
                )}
                {step === "address" && shipQuote && (
                  <div className="flex justify-between items-baseline mb-1.5" data-testid="shipping-row">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Shipping · {shipQuote.courier}</span>
                    <span className={shipQuote.charge > 0 ? "text-zinc-600" : "text-emerald-600"}>{shippingText(shipQuote)}</span>
                  </div>
                )}
                {step === "address" && payMethod === "cod" && codFeeAmt > 0 && (
                  <div className="flex justify-between items-baseline mb-1.5" data-testid="cod-fee-row">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">COD Fee</span>
                    <span className="text-zinc-600">{inr(codFeeAmt)}</span>
                  </div>
                )}
                <div className="flex justify-between items-baseline mb-6 mt-3">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Total</span>
                  <span className="font-serif text-3xl text-gold" data-testid="cart-total">{inr(displayTotal)}</span>
                </div>
                {step === "bag" ? (
                  <button
                    onClick={() => setStep("address")}
                    data-testid="cart-continue-btn"
                    className="w-full flex items-center justify-center gap-3 bg-gold text-white text-xs uppercase tracking-[0.3em] py-4 hover:bg-champagne transition-colors duration-300"
                  >
                    Continue to Address
                  </button>
                ) : (
                  <>
                    <div className="mb-4" data-testid="pay-methods">
                      <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2.5">Payment Method</p>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { key: "card", label: "Card", icon: CreditCard, show: true },
                          { key: "wallet", label: "Wallet", icon: Wallet, show: walletEligible },
                          { key: "cod", label: "COD", icon: Banknote, show: true },
                        ].filter((m) => m.show).map(({ key, label, icon: Icon }) => (
                          <button
                            key={key}
                            onClick={() => setPayMethod(key)}
                            data-testid={`pay-method-${key}`}
                            className={`flex flex-col items-center gap-1.5 border px-2 py-3 text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                              payMethod === key ? "border-gold bg-pink-50/60 text-gold" : "border-zinc-200 text-zinc-500 hover:border-gold/50"
                            }`}
                          >
                            <Icon size={15} /> {label}
                          </button>
                        ))}
                      </div>
                      {payMethod === "wallet" && (
                        <p className="text-[10px] text-zinc-400 mt-2" data-testid="wallet-balance-hint">{inr(wallet.balance)} available in your ViRa Wallet</p>
                      )}
                      {payMethod === "cod" && (
                        <p className="text-[10px] text-zinc-400 mt-2" data-testid="cod-hint">
                          Pay in cash or UPI when your order arrives{codFee > 0 ? ` — a ${inr(codFee)} COD fee applies` : ""}. Cashback is credited on delivery.
                        </p>
                      )}
                    </div>
                    <button
                      onClick={placeOrder}
                      disabled={loading}
                      data-testid={payMethod === "cod" ? "cart-cod-pay-btn" : payMethod === "wallet" ? "cart-wallet-pay-btn" : "cart-checkout-btn"}
                      className="w-full flex items-center justify-center gap-3 bg-gold text-white text-xs uppercase tracking-[0.3em] py-4 hover:bg-champagne transition-colors duration-300 disabled:opacity-50"
                    >
                      {payMethod === "cod" ? <Banknote size={13} /> : payMethod === "wallet" ? <Wallet size={13} /> : <Lock size={13} />}
                      {loading ? "Placing Order…" : payMethod === "cod" ? `Place Order — Pay ${inr(displayTotal)} on Delivery` : payMethod === "wallet" ? `Pay ${inr(finalTotal)} with Wallet` : "Pay Securely by Card"}
                    </button>
                    <button onClick={() => setStep("bag")} data-testid="cart-back-btn" className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-gold transition-colors duration-200">
                      <ArrowLeft size={12} /> Back to Bag
                    </button>
                  </>
                )}
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 text-center mt-4">
                  Powered by Stripe · COD available · Free shipping above ₹999
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
